import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../config/api';
import '../App.css';

// Gallery Image Card Component with Download & Share
const GalleryImageCard = ({ img, index }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        paddingTop: '100%', // Square aspect ratio
        borderRadius: '16px',
        overflow: 'hidden',
        cursor: 'pointer',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        transition: 'all 0.3s ease',
        background: '#f0f0f0',
        border: '2px solid transparent'
      }}
      onClick={() => window.open(img, '_blank')}
      onMouseEnter={(e) => {
        setIsHovered(true);
        e.currentTarget.style.transform = 'translateY(-8px) scale(1.02)';
        e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.25)';
        e.currentTarget.style.borderColor = '#3b82f6';
      }}
      onMouseLeave={(e) => {
        setIsHovered(false);
        e.currentTarget.style.transform = 'translateY(0) scale(1)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
        e.currentTarget.style.borderColor = 'transparent';
      }}
    >
      <img
        src={img}
        alt={`Gallery ${index + 1}`}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          transition: 'transform 0.3s ease'
        }}
        onError={(e) => {
          e.target.src = 'https://via.placeholder.com/200?text=Image';
        }}
        onMouseEnter={(e) => {
          e.target.style.transform = 'scale(1.1)';
        }}
        onMouseLeave={(e) => {
          e.target.style.transform = 'scale(1)';
        }}
      />
      {/* Overlay with Download & Share Icons on hover */}
      <div 
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.5) 100%)',
          opacity: isHovered ? 1 : 0,
          transition: 'opacity 0.3s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '16px',
          pointerEvents: isHovered ? 'auto' : 'none',
          borderRadius: '16px'
        }}
      >
        {/* Download Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            const link = document.createElement('a');
            link.href = img;
            link.download = `gallery-image-${index + 1}.jpg`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }}
          style={{
            width: '44px',
            height: '44px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.9)',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            fontSize: '1.3rem',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.1)';
            e.currentTarget.style.background = 'rgba(255, 255, 255, 1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
          }}
          title="Download Image"
        >
          ⬇️
        </button>

        {/* Share Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (navigator.share) {
              navigator.share({
                title: 'Gallery Image',
                text: 'Check out this image',
                url: img
              }).catch(err => console.log('Error sharing:', err));
            } else {
              // Fallback: Copy to clipboard
              navigator.clipboard.writeText(img).then(() => {
                alert('Image URL copied to clipboard!');
              }).catch(err => {
                console.log('Error copying:', err);
                // Fallback: Open in new tab
                window.open(img, '_blank');
              });
            }
          }}
          style={{
            width: '44px',
            height: '44px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.9)',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            fontSize: '1.3rem',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.1)';
            e.currentTarget.style.background = 'rgba(255, 255, 255, 1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
          }}
          title="Share Image"
        >
          🔗
        </button>
      </div>
    </div>
  );
};

// Slider Carousel Component
const SliderCarousel = ({ images }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  // Auto-play carousel
  useEffect(() => {
    if (images.length > 1 && !isPaused) {
      const interval = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
      }, 5000); // Change slide every 5 seconds
      return () => clearInterval(interval);
    }
  }, [images.length, isPaused]);

  return (
      <div style={{ position: 'relative', width: '100%', margin: '0 auto' }}>
      {/* Main Carousel Container */}
      <div 
        style={{ 
          position: 'relative', 
          width: '100%', 
          overflow: 'hidden', 
          background: '#f0f0f0', 
          height: 'clamp(300px, 50vh, 600px)',
          minHeight: '300px',
          maxHeight: '600px'
        }}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <div
          style={{
            display: 'flex',
            transition: 'transform 0.5s ease-in-out',
            transform: `translateX(-${currentIndex * (100 / images.length)}%)`,
            width: `${images.length * 100}%`,
            height: '100%'
          }}
        >
          {images.map((img, index) => {
            console.log(`Rendering slide ${index}:`, img);
            return (
            <div
              key={`slide-${index}-${img}`}
              style={{
                width: `${100 / images.length}%`,
                flexShrink: 0,
                position: 'relative',
                height: '100%',
                overflow: 'hidden',
                background: '#f0f0f0'
              }}
            >
              <img
                src={img}
                alt={`Slider ${index + 1}`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center',
                  display: 'block',
                  minWidth: '100%',
                  minHeight: '100%'
                }}
                onError={(e) => {
                  console.error(`Error loading image ${index + 1}:`, img);
                  e.target.style.display = 'block';
                  e.target.style.width = '100%';
                  e.target.style.height = '100%';
                  e.target.src = 'https://via.placeholder.com/1920x500?text=Image+Not+Found';
                }}
                onLoad={(e) => {
                  console.log(`Image ${index + 1} loaded successfully:`, img);
                  const imgElement = e.target;
                  imgElement.style.objectFit = 'cover';
                  imgElement.style.objectPosition = 'center';
                  imgElement.style.width = '100%';
                  imgElement.style.height = '100%';
                }}
              />
            </div>
            );
          })}
        </div>

        {/* Navigation Arrows - Responsive */}
        {images.length > 1 && (
          <>
            <button
              onClick={prevSlide}
              style={{
                position: 'absolute',
                left: 'clamp(8px, 2vw, 20px)',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: 'clamp(36px, 8vw, 50px)',
                height: 'clamp(36px, 8vw, 50px)',
                fontSize: 'clamp(18px, 4vw, 24px)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                transition: 'all 0.3s',
                touchAction: 'manipulation'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(0, 0, 0, 0.8)';
                e.target.style.transform = 'translateY(-50%) scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(0, 0, 0, 0.5)';
                e.target.style.transform = 'translateY(-50%) scale(1)';
              }}
            >
              ‹
            </button>
            <button
              onClick={nextSlide}
              style={{
                position: 'absolute',
                right: 'clamp(8px, 2vw, 20px)',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: 'clamp(36px, 8vw, 50px)',
                height: 'clamp(36px, 8vw, 50px)',
                fontSize: 'clamp(18px, 4vw, 24px)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                transition: 'all 0.3s',
                touchAction: 'manipulation'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(0, 0, 0, 0.8)';
                e.target.style.transform = 'translateY(-50%) scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(0, 0, 0, 0.5)';
                e.target.style.transform = 'translateY(-50%) scale(1)';
              }}
            >
              ›
            </button>
          </>
        )}

        {/* Slide Indicators - Responsive */}
        {images.length > 1 && (
          <div style={{
            position: 'absolute',
            bottom: 'clamp(10px, 2vh, 20px)',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 'clamp(6px, 1.5vw, 10px)',
            zIndex: 10,
            padding: '0 10px'
          }}>
            {images.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                style={{
                  width: currentIndex === index ? 'clamp(24px, 5vw, 30px)' : 'clamp(8px, 2vw, 12px)',
                  height: 'clamp(8px, 2vw, 12px)',
                  borderRadius: '6px',
                  border: 'none',
                  background: currentIndex === index ? '#3b82f6' : 'rgba(255, 255, 255, 0.5)',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                  touchAction: 'manipulation'
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const ProfileView = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [submittingSurvey, setSubmittingSurvey] = useState({});
  const [surveyMessage, setSurveyMessage] = useState({});
  const [submittedSurveys, setSubmittedSurveys] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [showSurveyModal, setShowSurveyModal] = useState(false);
  const [respondentIdentifier, setRespondentIdentifier] = useState(null);
  const [shareMessage, setShareMessage] = useState('');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [showFlashImage, setShowFlashImage] = useState(true);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackForm, setFeedbackForm] = useState({ name: '', mobile_number: '', address: '', description: '' });
  const [feedbackImageUrls, setFeedbackImageUrls] = useState([]); // Array of uploaded image URLs
  const feedbackImageUrlsRef = useRef([]); // Use ref to persist image URLs
  const [feedbackImageFiles, setFeedbackImageFiles] = useState([]); // Array of selected files
  const [feedbackImagePreviews, setFeedbackImagePreviews] = useState([]); // Array of preview URLs
  const [uploadingFeedbackImage, setUploadingFeedbackImage] = useState(false);
  const fileInputRef = useRef(null); // Ref for file input to reset it
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState(null);
  // Removed playingReels state - iframe will be directly interactive

  // Generate or retrieve unique identifier for this browser/user
  useEffect(() => {
    // Generate a unique identifier (UUID) and store it in localStorage for persistence
    // This is only for identification, not for tracking submitted status
    let identifier = localStorage.getItem('respondent_identifier');
    if (!identifier) {
      // Generate a simple UUID-like identifier
      identifier = 'resp_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('respondent_identifier', identifier);
    }
    setRespondentIdentifier(identifier);
  }, []);

  useEffect(() => {
    if (respondentIdentifier) {
      fetchProfile();
    }
  }, [username, respondentIdentifier]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      
      // Check if profile was already viewed in this session
      const viewedProfiles = JSON.parse(sessionStorage.getItem('viewedProfiles') || '[]');
      const shouldIncrementView = !viewedProfiles.includes(username);
      
      // Add query parameter to indicate if we should increment view count
      const url = `${API_BASE_URL}/api/profiles/username/${username}${shouldIncrementView ? '?incrementView=true' : '?incrementView=false'}`;
      const response = await fetch(url);
      const data = await response.json();
      
      // Mark this profile as viewed in this session
      if (shouldIncrementView && data.success) {
        viewedProfiles.push(username);
        sessionStorage.setItem('viewedProfiles', JSON.stringify(viewedProfiles));
      }

      if (data.success) {
        // Ensure arrays are properly formatted
        const profileData = {
          ...data.data,
          flash_image: data.data.flash_image || '',
          slider_images: Array.isArray(data.data.slider_images) ? data.data.slider_images : (typeof data.data.slider_images === 'string' ? JSON.parse(data.data.slider_images) : []),
          image_gallery: Array.isArray(data.data.image_gallery) ? data.data.image_gallery : (typeof data.data.image_gallery === 'string' ? JSON.parse(data.data.image_gallery) : []),
          video_gallery: Array.isArray(data.data.video_gallery) ? data.data.video_gallery : (typeof data.data.video_gallery === 'string' ? JSON.parse(data.data.video_gallery) : []),
          instagram_reels: Array.isArray(data.data.instagram_reels) ? data.data.instagram_reels : (typeof data.data.instagram_reels === 'string' ? JSON.parse(data.data.instagram_reels) : []),
          recent_news: Array.isArray(data.data.recent_news) ? data.data.recent_news : (typeof data.data.recent_news === 'string' ? JSON.parse(data.data.recent_news) : []),
          social_links: typeof data.data.social_links === 'string' ? JSON.parse(data.data.social_links) : (data.data.social_links || {}),
          surveys: Array.isArray(data.data.surveys) ? data.data.surveys : (typeof data.data.surveys === 'string' ? JSON.parse(data.data.surveys) : []),
          feedbacks: Array.isArray(data.data.feedbacks) ? data.data.feedbacks : (typeof data.data.feedbacks === 'string' ? JSON.parse(data.data.feedbacks) : [])
        };
        console.log('Profile data loaded:', { 
          hasFlashImage: !!profileData.flash_image, 
          flashImage: profileData.flash_image,
          instagram_reels: profileData.instagram_reels,
          instagram_reels_type: typeof profileData.instagram_reels,
          instagram_reels_length: profileData.instagram_reels ? profileData.instagram_reels.length : 0,
          instagram_reels_isArray: Array.isArray(profileData.instagram_reels),
          social_links: profileData.social_links,
          youtube_url: profileData.social_links?.youtube,
          instagram_url: profileData.social_links?.instagram
        });
        setProfile(profileData);
        // Show flash image if it exists
        if (profileData.flash_image && profileData.flash_image.trim() !== '') {
          setShowFlashImage(true);
        }
        
        // Load submitted surveys from database
        if (respondentIdentifier && profileData.id) {
          await fetchSubmittedSurveys(profileData.id, respondentIdentifier);
        } else {
          setSubmittedSurveys({});
        }
      } else {
        setError(data.message || 'Profile not found');
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      setError('Error loading profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Feedback image upload handler - handles multiple images
  const handleFeedbackImageChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Validate all files
    const validFiles = [];
    const invalidFiles = [];

    files.forEach(file => {
      if (!file.type.match('image.*')) {
        invalidFiles.push(`${file.name} - Not an image file`);
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        invalidFiles.push(`${file.name} - File size exceeds 5MB`);
        return;
      }
      
      validFiles.push(file);
    });

    if (invalidFiles.length > 0) {
      setFeedbackMessage({ type: 'error', text: `Invalid files: ${invalidFiles.join(', ')}` });
      setTimeout(() => setFeedbackMessage(null), 3000);
    }

    if (validFiles.length === 0) return;

    // Add files to state
    setFeedbackImageFiles(prev => [...prev, ...validFiles]);
    
    // Create previews
    const newPreviews = [];
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFeedbackImagePreviews(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
      newPreviews.push(file);
    });

    // Automatically upload all images when selected
    console.log(`${validFiles.length} image(s) selected, auto-uploading...`);
    setUploadingFeedbackImage(true);
    setFeedbackMessage(null);
    
    const uploadPromises = validFiles.map(async (file) => {
      try {
        const uploadFormData = new FormData();
        uploadFormData.append('image', file);

        const response = await fetch(`${API_BASE_URL}/api/upload/feedback`, {
          method: 'POST',
          body: uploadFormData
        });

        const data = await response.json();

        if (response.ok && data.success && data.data && data.data.url) {
          const imageUrl = data.data.url;
          console.log('=== AUTO-UPLOAD SUCCESS ===');
          console.log('Image URL received from server:', imageUrl);
          return imageUrl;
        } else {
          console.error('Auto-upload failed for file:', file.name, data);
          return null;
        }
      } catch (error) {
        console.error('Error auto-uploading feedback image:', file.name, error);
        return null;
      }
    });

    try {
      const uploadedUrls = await Promise.all(uploadPromises);
      const successfulUrls = uploadedUrls.filter(url => url !== null);
      
      if (successfulUrls.length > 0) {
        // Store in both state and ref for reliability
        setFeedbackImageUrls(prev => [...prev, ...successfulUrls]);
        feedbackImageUrlsRef.current = [...feedbackImageUrlsRef.current, ...successfulUrls];
        
        console.log(`${successfulUrls.length} image(s) uploaded successfully`);
        console.log('Image URLs stored:', successfulUrls);
        
        setFeedbackMessage({ 
          type: 'success', 
          text: `${successfulUrls.length} image(s) uploaded successfully!` 
        });
        setTimeout(() => {
          setFeedbackMessage(null);
        }, 2000);
        
        // Reset file input to allow selecting same file again
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        setFeedbackMessage({ type: 'error', text: 'Failed to upload images. Please try again.' });
        // Reset file input even on error
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    } catch (error) {
      console.error('Error uploading images:', error);
      setFeedbackMessage({ type: 'error', text: 'Error uploading images. Please try again.' });
      // Reset file input on error
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } finally {
      setUploadingFeedbackImage(false);
    }
  };

  // Remove image from feedback
  const handleRemoveFeedbackImage = (index) => {
    setFeedbackImageUrls(prev => prev.filter((_, i) => i !== index));
    feedbackImageUrlsRef.current = feedbackImageUrlsRef.current.filter((_, i) => i !== index);
    setFeedbackImageFiles(prev => prev.filter((_, i) => i !== index));
    setFeedbackImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  // Submit feedback
  const handleSubmitFeedback = async (e) => {
    e.preventDefault();
    
    if (!feedbackForm.name.trim() || !feedbackForm.mobile_number.trim() || !feedbackForm.address.trim() || !feedbackForm.description.trim()) {
      setFeedbackMessage({ type: 'error', text: 'Please fill all required fields' });
      return;
    }

    if (!profile || !profile.id) {
      setFeedbackMessage({ type: 'error', text: 'Profile not found' });
      return;
    }

    setSubmittingFeedback(true);
    setFeedbackMessage(null);

    // Debug: Log form data before submitting
    console.log('=== SUBMITTING FEEDBACK ===');
    console.log('Form state:', feedbackForm);
    console.log('Image files:', feedbackImageFiles);
    console.log('Image URLs state:', feedbackImageUrls);
    console.log('Image URLs ref:', feedbackImageUrlsRef.current);
    
    try {
      let imageValue = null;
      
      // Get image URLs from ref first (most reliable), fallback to state
      const imageUrlsFromRef = feedbackImageUrlsRef.current || [];
      const imageUrlsFromState = feedbackImageUrls || [];
      
      // Prefer ref value, then state value
      const finalImageUrls = imageUrlsFromRef.length > 0 ? imageUrlsFromRef : imageUrlsFromState;
      
      console.log('Image URLs from ref:', imageUrlsFromRef);
      console.log('Image URLs from state:', imageUrlsFromState);
      console.log('Final image URLs to use:', finalImageUrls);
      
      // If there are unuploaded files, upload them first
      if (feedbackImageFiles.length > finalImageUrls.length) {
        console.log('Some images not uploaded yet. Uploading now...');
        const unuploadedFiles = feedbackImageFiles.slice(finalImageUrls.length);
        
        const uploadPromises = unuploadedFiles.map(async (file) => {
          try {
            const uploadFormData = new FormData();
            uploadFormData.append('image', file);

            const uploadResponse = await fetch(`${API_BASE_URL}/api/upload/feedback`, {
              method: 'POST',
              body: uploadFormData
            });

            const uploadData = await uploadResponse.json();

            if (uploadResponse.ok && uploadData.success && uploadData.data && uploadData.data.url) {
              return uploadData.data.url;
            }
            return null;
          } catch (error) {
            console.error('Error uploading image during submit:', error);
            return null;
          }
        });

        const newlyUploadedUrls = await Promise.all(uploadPromises);
        const successfulNewUrls = newlyUploadedUrls.filter(url => url !== null);
        
        if (successfulNewUrls.length > 0) {
          const allUrls = [...finalImageUrls, ...successfulNewUrls];
          setFeedbackImageUrls(allUrls);
          feedbackImageUrlsRef.current = allUrls;
          finalImageUrls.push(...successfulNewUrls);
        }
      }
      
      // Store images as JSON array (or single image if only one)
      if (finalImageUrls.length > 0) {
        const validUrls = finalImageUrls.filter(url => url && url.trim() !== '' && url !== 'null' && url !== 'undefined');
        if (validUrls.length > 0) {
          // Store as JSON array
          imageValue = JSON.stringify(validUrls);
        }
      }
      
      console.log('Final image value to submit:', imageValue);
      
      const submitData = {
        profile_id: profile.id,
        name: feedbackForm.name.trim(),
        mobile_number: feedbackForm.mobile_number.trim(),
        address: feedbackForm.address.trim(),
        image: imageValue,
        description: feedbackForm.description.trim()
      };
      
      console.log('Complete submit data:', JSON.stringify(submitData, null, 2));
      console.log('========================');
      
      const response = await fetch(`${API_BASE_URL}/api/feedbacks/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setFeedbackMessage({ type: 'success', text: 'Feedback submitted successfully! Thank you for your feedback.' });
        
        // Reset form
        setFeedbackForm({ name: '', mobile_number: '', address: '', description: '' });
        setFeedbackImageUrls([]); // Reset image URLs state
        feedbackImageUrlsRef.current = []; // Reset image URLs ref
        setFeedbackImageFiles([]);
        setFeedbackImagePreviews([]);
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        
        // Close modal and refresh profile after 2 seconds
        setTimeout(() => {
          setShowFeedbackModal(false);
          setFeedbackMessage(null);
          fetchProfile();
        }, 2000);
      } else {
        setFeedbackMessage({ type: 'error', text: data.message || 'Error submitting feedback. Please try again.' });
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setFeedbackMessage({ type: 'error', text: 'Error submitting feedback. Please try again.' });
    } finally {
      setSubmittingFeedback(false);
    }
  };

  // Fetch submitted surveys from database
  const fetchSubmittedSurveys = async (profileId, identifier) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/survey-responses/check-submitted?profile_id=${profileId}&respondent_identifier=${encodeURIComponent(identifier)}`
      );
      const data = await response.json();

      if (data.success && data.data.submitted_survey_indices) {
        const submitted = {};
        data.data.submitted_survey_indices.forEach((index) => {
          submitted[index] = true;
        });
        console.log('Loaded submitted surveys from database:', submitted);
        setSubmittedSurveys(submitted);
      } else {
        setSubmittedSurveys({});
      }
    } catch (err) {
      console.error('Error fetching submitted surveys:', err);
      setSubmittedSurveys({});
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#f0f4f8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner"></div>
          <p style={{ marginTop: '20px', color: '#333', fontSize: '1.2rem' }}>Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div style={{ minHeight: '100vh', background: '#f0f4f8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', padding: '40px', background: 'white', borderRadius: '20px', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
          <h2 style={{ color: '#dc3545', marginBottom: '16px', fontSize: '2rem' }}>Profile Not Found</h2>
          <p style={{ color: '#666', marginBottom: '24px', fontSize: '1.1rem' }}>{error || 'The profile you are looking for does not exist.'}</p>
          <Link to="/" style={{ padding: '12px 24px', background: '#667eea', color: 'white', textDecoration: 'none', borderRadius: '8px', fontWeight: '600' }}>
            Go to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #e0f2fe 0%, #f0f9ff 100%)'
    }}>
      {/* View Count - Top Right Corner */}
      {profile && profile.view_count !== undefined && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '10px 16px',
          borderRadius: '25px',
          display: 'flex',
          alignItems: 'center',
          fontSize: '0.95rem',
          fontWeight: '600',
          boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
          zIndex: 1000,
          transition: 'transform 0.2s ease',
          cursor: 'default'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.05)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
        }}
        >
          <span>{profile.view_count || 0} {profile.view_count === 1 ? 'View' : 'Views'}</span>
        </div>
      )}

      {/* Top Logo Section - Centered */}
      <div style={{
        background: 'linear-gradient(135deg, #e0f2fe 0%, #f0f9ff 100%)',
        padding: '20px 20px 0 20px',
        marginTop: '5px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '20px',
          padding: '20px 40px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          maxWidth: '1000px',
          width: '100%',
          margin: '0 auto',
          position: 'relative',
          zIndex: 1
        }}>
          {/* Logo - Without Circular Background */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            padding: '10px'
          }}>
            {profile.logo_url && profile.logo_url.trim() !== '' ? (
              <img
                src={profile.logo_url}
                alt="Party Logo"
                style={{
                  maxWidth: '120px',
                  maxHeight: '120px',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                  display: 'block'
                }}
                onError={(e) => {
                  console.error('Error loading logo:', profile.logo_url);
                  e.target.style.display = 'none';
                }}
                onLoad={(e) => {
                  console.log('Logo loaded successfully:', profile.logo_url);
                }}
              />
            ) : (
              <div style={{
                fontSize: '3rem',
                color: '#1a1d29',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '110px',
                height: '110px'
              }}>🪷</div>
            )}
          </div>

          {/* Vertical Separator Bar */}
          <div style={{
            width: '5px',
            height: '80px',
            borderRadius: '10px',
            background: 'linear-gradient(180deg, #3b82f6 0%, #ef4444 100%)',
            flexShrink: 0
          }}></div>

          {/* Party Name - Next to Logo */}
          <div style={{ 
            flex: 1,
            minWidth: 0,
            overflow: 'hidden'
          }}>
            <h1 style={{
              fontSize: '2.5rem',
              color: '#3b82f6',
              fontWeight: '700',
              margin: 0,
              lineHeight: '1.2',
              textAlign: 'left',
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
              whiteSpace: 'normal'
            }}>
              {profile.party_name || 'Political Party'}
            </h1>
          </div>
        </div>
      </div>

      {/* Flash Image Popup Modal - Overlay Style */}
      {profile.flash_image && profile.flash_image.trim() !== '' && showFlashImage && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px',
            animation: 'fadeIn 0.3s ease'
          }}
          onClick={(e) => {
            // Close on background click
            if (e.target === e.currentTarget) {
              setShowFlashImage(false);
            }
          }}
        >
          <style>{`
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            @keyframes slideUp {
              from { 
                opacity: 0;
                transform: translateY(30px);
              }
              to { 
                opacity: 1;
                transform: translateY(0);
              }
            }
          `}</style>
          
          <div
            style={{
              position: 'relative',
              maxWidth: '500px',
              width: '85%',
              maxHeight: '60vh',
              background: 'transparent',
              borderRadius: '12px',
              animation: 'slideUp 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '80px auto 0 auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setShowFlashImage(false)}
              style={{
                position: 'absolute',
                top: '-15px',
                right: '-15px',
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: '#ef4444',
                color: 'white',
                border: '3px solid white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '24px',
                fontWeight: 'bold',
                boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
                transition: 'all 0.3s ease',
                zIndex: 10000,
                lineHeight: '1'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#dc2626';
                e.target.style.transform = 'scale(1.15) rotate(90deg)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#ef4444';
                e.target.style.transform = 'scale(1) rotate(0deg)';
              }}
              title="Close"
            >
              ×
            </button>

            {/* Flash Image */}
            <img
              src={profile.flash_image.startsWith('http') ? profile.flash_image : `${API_BASE_URL}${profile.flash_image}`}
              alt="Flash Banner"
              style={{
                maxWidth: '100%',
                maxHeight: '60vh',
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                borderRadius: '12px',
                display: 'block',
                cursor: 'pointer',
                boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
                transition: 'transform 0.3s ease'
              }}
              onClick={() => {
                const imageUrl = profile.flash_image.startsWith('http') ? profile.flash_image : `${API_BASE_URL}${profile.flash_image}`;
                window.open(imageUrl, '_blank');
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.02)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
              }}
              onError={(e) => {
                console.error('Flash image load error:', profile.flash_image);
                e.target.style.display = 'none';
              }}
              onLoad={() => {
                console.log('Flash image loaded successfully:', profile.flash_image);
              }}
            />
          </div>
        </div>
      )}

      {/* Carousel Section - Below Logo/Flash Image */}
      <div id="home"></div>
      {profile.slider_images && profile.slider_images.length > 0 && (
        <div style={{
          padding: '0',
          marginTop: '5px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%'
        }}>
          <SliderCarousel images={profile.slider_images} />
        </div>
      )}

      {/* Contact Information Card - Responsive: 2 Columns on Desktop, Stacked on Mobile */}
      <div style={{
        maxWidth: '800px',
        width: '100%',
        margin: '10px auto 24px',
        background: 'white',
        borderRadius: '20px',
        padding: '32px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
        position: 'relative',
        zIndex: 10
      }}>
        <style>{`
          @media (max-width: 768px) {
            .contact-card-container {
              flex-direction: column !important;
              gap: 24px !important;
            }
            .contact-card-image {
              flex: 1 1 auto !important;
              min-height: auto !important;
              width: 100% !important;
            }
            .contact-card-info {
              width: 100% !important;
            }
          }
        `}</style>
        <div className="contact-card-container" style={{ display: 'flex', gap: '32px', alignItems: 'flex-start', flexDirection: 'row' }}>
          {/* Left Column - Profile Picture (Top on Mobile) */}
          <div className="contact-card-image" style={{ 
            flex: '0 0 200px', 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            minHeight: '200px',
            width: 'auto'
          }}>
            {(profile.profile_picture && profile.profile_picture.trim() !== '') ? (
              <img
                src={profile.profile_picture}
                alt={profile.name}
                style={{
                  width: '150px',
                  height: '150px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '4px solid #e0e7ff',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <div style={{
                width: '150px',
                height: '150px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '3.5rem',
                fontWeight: '700',
                border: '4px solid #e0e7ff',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              }}>
                {profile.name?.charAt(0)?.toUpperCase() || 'P'}
              </div>
            )}
          </div>

          {/* Right Column - Contact Information (Below on Mobile) */}
          <div className="contact-card-info" style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'stretch', width: 'auto' }}>
            {/* Name Button */}
            <button style={{
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              padding: '14px 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
              transition: 'all 0.3s',
              width: '100%'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
            }}
            >
              <span>{profile.name}</span>
            </button>

            {/* Address Button */}
            {profile.address && (
              <button style={{
                background: 'white',
                color: '#333',
                border: '2px solid #e5e7eb',
                borderRadius: '12px',
                padding: '12px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                fontSize: '0.95rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.3s',
                textAlign: 'left',
                width: '100%'
              }}
              onMouseEnter={(e) => {
                e.target.style.borderColor = '#3b82f6';
                e.target.style.background = '#f0f4ff';
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.background = 'white';
              }}
              >
                <span style={{ fontSize: '1.2rem' }}>📍</span>
                <span style={{ flex: 1, textAlign: 'left' }}>{profile.address}</span>
              </button>
            )}

            {/* Phone Button */}
            {profile.mobile_number && (
              <button style={{
                background: 'white',
                color: '#333',
                border: '2px solid #e5e7eb',
                borderRadius: '12px',
                padding: '12px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                fontSize: '0.95rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.3s',
                textAlign: 'left',
                width: '100%'
              }}
              onClick={() => window.location.href = `tel:${profile.mobile_number}`}
              onMouseEnter={(e) => {
                e.target.style.borderColor = '#3b82f6';
                e.target.style.background = '#f0f4ff';
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.background = 'white';
              }}
              >
                <span style={{ fontSize: '1.2rem' }}>📱</span>
                <span style={{ flex: 1, textAlign: 'left' }}>{profile.mobile_number}</span>
              </button>
            )}

            {/* Email Button */}
            {profile.email_id && (
              <button style={{
                background: 'white',
                color: '#333',
                border: '2px solid #e5e7eb',
                borderRadius: '12px',
                padding: '12px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                fontSize: '0.95rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.3s',
                textAlign: 'left',
                width: '100%'
              }}
              onClick={() => window.location.href = `mailto:${profile.email_id}`}
              onMouseEnter={(e) => {
                e.target.style.borderColor = '#3b82f6';
                e.target.style.background = '#f0f4ff';
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.background = 'white';
              }}
              >
                <span style={{ fontSize: '1.2rem' }}>✉️</span>
                <span style={{ flex: 1, wordBreak: 'break-word', textAlign: 'left' }}>{profile.email_id}</span>
              </button>
            )}
          </div>
        </div>

        {/* Social Media Icons */}
        {(profile.social_links?.facebook || profile.social_links?.twitter || profile.social_links?.instagram || profile.social_links?.youtube || profile.social_links?.website || profile.social_links?.linkedin) && (
          <div style={{ 
            display: 'flex', 
            gap: '12px', 
            justifyContent: 'center',
            flexWrap: 'wrap',
            paddingTop: '16px',
            borderTop: '1px solid #e5e7eb'
          }}>
            {profile.social_links.facebook && (
              <a 
                href={profile.social_links.facebook} 
                target="_blank" 
                rel="noopener noreferrer"
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '50%',
                  background: '#1877f2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textDecoration: 'none',
                  boxShadow: '0 4px 12px rgba(24, 119, 242, 0.3)',
                  transition: 'all 0.3s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'scale(1.1)';
                  e.target.style.boxShadow = '0 6px 16px rgba(24, 119, 242, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'scale(1)';
                  e.target.style.boxShadow = '0 4px 12px rgba(24, 119, 242, 0.3)';
                }}
                title="Facebook"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M13.397 20.997v-8.196h2.765l.411-3.209h-3.176V7.548c0-.926.258-1.56 1.587-1.56h1.684V3.127A22.336 22.336 0 0 0 14.201 3c-2.444 0-4.122 1.492-4.122 4.231v2.355H7.332v3.209h2.753v8.202h3.312z" fill="#FFFFFF"/>
                </svg>
              </a>
            )}
            {profile.social_links.twitter && (
              <a 
                href={profile.social_links.twitter} 
                target="_blank" 
                rel="noopener noreferrer"
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '50%',
                  background: '#1da1f2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem',
                  textDecoration: 'none',
                  boxShadow: '0 4px 12px rgba(29, 161, 242, 0.3)',
                  transition: 'all 0.3s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'scale(1.1)';
                  e.target.style.boxShadow = '0 6px 16px rgba(29, 161, 242, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'scale(1)';
                  e.target.style.boxShadow = '0 4px 12px rgba(29, 161, 242, 0.3)';
                }}
                title="Twitter"
              >
                🐦
              </a>
            )}
            {profile.social_links.instagram && (
              <a 
                href={profile.social_links.instagram} 
                target="_blank" 
                rel="noopener noreferrer"
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '50%',
                  background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textDecoration: 'none',
                  boxShadow: '0 4px 12px rgba(188, 24, 136, 0.3)',
                  transition: 'all 0.3s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'scale(1.1)';
                  e.target.style.boxShadow = '0 6px 16px rgba(188, 24, 136, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'scale(1)';
                  e.target.style.boxShadow = '0 4px 12px rgba(188, 24, 136, 0.3)';
                }}
                title="Instagram"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4 2.209 0 4 1.791 4 4 0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" fill="#FFFFFF"/>
                </svg>
              </a>
            )}
            {profile.social_links.youtube && (
              <a 
                href={profile.social_links.youtube} 
                target="_blank" 
                rel="noopener noreferrer"
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '50%',
                  background: '#ff0000',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem',
                  textDecoration: 'none',
                  boxShadow: '0 4px 12px rgba(255, 0, 0, 0.3)',
                  transition: 'all 0.3s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'scale(1.1)';
                  e.target.style.boxShadow = '0 6px 16px rgba(255, 0, 0, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'scale(1)';
                  e.target.style.boxShadow = '0 4px 12px rgba(255, 0, 0, 0.3)';
                }}
                title="YouTube"
              >
                ▶️
              </a>
            )}
            {profile.social_links.website && (
              <a 
                href={profile.social_links.website} 
                target="_blank" 
                rel="noopener noreferrer"
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '50%',
                  background: '#3b82f6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem',
                  textDecoration: 'none',
                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                  transition: 'all 0.3s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'scale(1.1)';
                  e.target.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'scale(1)';
                  e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                }}
                title="Website"
              >
                🌐
              </a>
            )}
            {profile.social_links.linkedin && (
              <a 
                href={profile.social_links.linkedin} 
                target="_blank" 
                rel="noopener noreferrer"
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '50%',
                  background: '#0077b5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem',
                  textDecoration: 'none',
                  boxShadow: '0 4px 12px rgba(0, 119, 181, 0.3)',
                  transition: 'all 0.3s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'scale(1.1)';
                  e.target.style.boxShadow = '0 6px 16px rgba(0, 119, 181, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'scale(1)';
                  e.target.style.boxShadow = '0 4px 12px rgba(0, 119, 181, 0.3)';
                }}
                title="LinkedIn"
              >
                💼
              </a>
            )}
          </div>
        )}
      </div>

      {/* Main Content Container */}
      <div style={{ width: '100%', margin: '0 auto', padding: '0 20px' }}>
        {/* Work Report Section */}
        {profile.about_us && (
          <div id="about" style={{
            maxWidth: '800px',
            width: '100%',
            margin: '10px auto 24px',
            background: 'white',
            borderRadius: '20px',
            padding: '32px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            position: 'relative',
            zIndex: 10
          }}>
            <h2 style={{
              fontSize: '1.3rem',
              color: '#1a1d29',
              marginBottom: '16px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span style={{ fontSize: '1.5rem' }}>👤</span>
              कार्य वृतांत
            </h2>
            <p style={{ color: '#666', lineHeight: '1.7', fontSize: '0.95rem', margin: 0 }}>
              {profile.about_us}
            </p>
          </div>
        )}

        {/* Detailed Description Section */}
        {profile.description && (
          <div id="description" style={{
            maxWidth: '800px',
            width: '100%',
            margin: '10px auto 24px',
            background: 'white',
            borderRadius: '20px',
            padding: '32px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            position: 'relative',
            zIndex: 10
          }}>
            <h2 style={{
              fontSize: '1.3rem',
              color: '#1a1d29',
              marginBottom: '16px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span style={{ fontSize: '1.5rem' }}>📝</span>
              दैनंदिन दिनक्रम
            </h2>
            <p style={{ color: '#666', lineHeight: '1.7', fontSize: '0.95rem', margin: 0, whiteSpace: 'pre-wrap' }}>
              {profile.description}
            </p>
          </div>
        )}

        {/* Work Gallery Section (Image Gallery) - Fancy Card Layout */}
        {profile.image_gallery && profile.image_gallery.length > 0 && (
          <div id="photos" style={{
            maxWidth: '800px',
            width: '100%',
            margin: '10px auto 24px',
            background: 'white',
            borderRadius: '20px',
            padding: '32px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            position: 'relative',
            zIndex: 10
          }}>
            <h2 style={{
              fontSize: '1.3rem',
              color: '#1a1d29',
              marginBottom: '24px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span style={{ fontSize: '1.5rem' }}>📷</span>
              कार्य आढावा
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: '16px'
            }}>
              {profile.image_gallery.map((img, index) => (
                <GalleryImageCard key={index} img={img} index={index} />
              ))}
            </div>
          </div>
        )}

        {/* Current Affairs Section (Recent News) */}
        {profile.recent_news && profile.recent_news.length > 0 && (
          <div id="news" style={{
            maxWidth: '800px',
            width: '100%',
            margin: '10px auto 24px',
            background: 'white',
            borderRadius: '20px',
            padding: '32px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            position: 'relative',
            zIndex: 10
          }}>
            <h2 style={{
              fontSize: '1.3rem',
              color: '#1a1d29',
              marginBottom: '20px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span style={{ fontSize: '1.5rem' }}>📰</span>
              चालू घडामोडी
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '20px'
            }}>
              {profile.recent_news.map((news, index) => (
                <div
                  key={index}
                  style={{
                    background: 'white',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    border: '1px solid #e5e7eb',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.15)';
                    e.currentTarget.style.borderColor = '#3b82f6';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                    e.currentTarget.style.borderColor = '#e5e7eb';
                  }}
                >
                  {/* News Image */}
                  <div style={{
                    width: '100%',
                    height: '200px',
                    background: '#f0f0f0',
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                    {news.image ? (
                      <img
                        src={news.image}
                        alt={news.title || 'News'}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          transition: 'transform 0.3s ease'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
                          e.target.parentElement.innerHTML = '<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: white; font-size: 2rem;">📰</div>';
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'scale(1)';
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '3rem'
                      }}>
                        📰
                      </div>
                    )}
                  </div>
                  
                  {/* News Content */}
                  <div style={{ padding: '16px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                    {news.title && (
                      <h3 style={{
                        color: '#1a1d29',
                        marginBottom: '8px',
                        fontSize: '1rem',
                        fontWeight: '600',
                        lineHeight: '1.4',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {news.title}
                      </h3>
                    )}
                    {news.description && (
                      <p style={{
                        color: '#666',
                        lineHeight: '1.6',
                        fontSize: '0.85rem',
                        margin: 0,
                        flex: 1,
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {news.description}
                      </p>
                    )}
                    {news.date && (
                      <div style={{
                        marginTop: '12px',
                        paddingTop: '12px',
                        borderTop: '1px solid #e5e7eb',
                        color: '#999',
                        fontSize: '0.75rem'
                      }}>
                        📅 {news.date}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Video Gallery Section */}
        {profile.video_gallery && profile.video_gallery.length > 0 && (
          <div id="video-gallery" style={{
            maxWidth: '800px',
            width: '100%',
            margin: '10px auto 24px',
            background: 'white',
            borderRadius: '20px',
            padding: '32px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            position: 'relative',
            zIndex: 10
          }}>
            <h2 style={{
              fontSize: '1.3rem',
              color: '#1a1d29',
              marginBottom: '24px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span style={{ fontSize: '1.5rem' }}>▶️</span>
              व्हिडीओ गॅलरी
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '20px'
            }}>
              {profile.video_gallery.map((video, index) => {
                // Convert YouTube URL to embed format
                const getEmbedUrl = (url) => {
                  if (!url) return null;
                  
                  // Check if already embed URL
                  if (url.includes('youtube.com/embed/')) {
                    return url;
                  }
                  
                  // Extract video ID from various YouTube URL formats
                  let videoId = null;
                  
                  // youtube.com/watch?v=VIDEO_ID
                  const watchMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
                  if (watchMatch) {
                    videoId = watchMatch[1];
                  }
                  
                  // youtube.com/embed/VIDEO_ID
                  const embedMatch = url.match(/youtube\.com\/embed\/([^&\n?#]+)/);
                  if (embedMatch) {
                    videoId = embedMatch[1];
                  }
                  
                  if (videoId) {
                    return `https://www.youtube.com/embed/${videoId}`;
                  }
                  
                  return null;
                };
                
                const embedUrl = getEmbedUrl(video.url);
                
                return (
                  <div
                    key={index}
                    style={{
                      background: 'white',
                      borderRadius: '16px',
                      overflow: 'hidden',
                      border: '1px solid #e5e7eb',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.15)';
                      e.currentTarget.style.borderColor = '#3b82f6';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                      e.currentTarget.style.borderColor = '#e5e7eb';
                    }}
                  >
                    {/* Video Embed */}
                    {embedUrl ? (
                      <div style={{
                        position: 'relative',
                        width: '100%',
                        paddingTop: '56.25%', // 16:9 aspect ratio
                        background: '#000',
                        overflow: 'hidden'
                      }}>
                        <iframe
                          src={embedUrl}
                          title={video.title || `Video ${index + 1}`}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            border: 'none',
                            borderRadius: '16px 16px 0 0'
                          }}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    ) : (
                      <div style={{
                        position: 'relative',
                        width: '100%',
                        paddingTop: '56.25%', // 16:9 aspect ratio
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '1rem'
                      }}>
                        Invalid Video URL
                      </div>
                    )}
                    
                    {/* Video Title */}
                    {video.title && (
                      <div style={{
                        padding: '16px',
                        background: 'white'
                      }}>
                        <h3 style={{
                          color: '#1a1d29',
                          margin: 0,
                          fontSize: '1rem',
                          fontWeight: '600',
                          lineHeight: '1.4',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {video.title}
                        </h3>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Instagram Reels Section */}
        {profile.instagram_reels && profile.instagram_reels.length > 0 && (
          <div style={{
            maxWidth: '800px',
            width: '100%',
            margin: '10px auto 24px',
            background: 'white',
            borderRadius: '20px',
            padding: '32px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            position: 'relative',
            zIndex: 10
          }}>
            <h2 style={{
              fontSize: '1.3rem',
              color: '#1a1d29',
              marginBottom: '24px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span style={{ fontSize: '1.5rem' }}>📱</span>
              Instagram Reels
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '20px'
            }}>
              {profile.instagram_reels.map((reel, index) => {
                // Extract Instagram username from URL if possible
                const getInstagramUsername = (url) => {
                  if (!url) return 'instagram';
                  const match = url.match(/instagram\.com\/([^\/\?]+)/);
                  return match ? match[1] : 'instagram';
                };

                // Convert Instagram URL to embed format
                const getInstagramEmbedUrl = (url) => {
                  if (!url) return null;
                  
                  // Clean the URL first - remove query parameters and fragments
                  let cleanUrl = url.trim();
                  
                  // Remove query parameters and fragments to get clean URL
                  if (cleanUrl.includes('?')) {
                    cleanUrl = cleanUrl.split('?')[0];
                  }
                  if (cleanUrl.includes('#')) {
                    cleanUrl = cleanUrl.split('#')[0];
                  }
                  
                  // Remove trailing slash
                  cleanUrl = cleanUrl.replace(/\/$/, '');
                  
                  // Check if already embed URL - use directly
                  if (cleanUrl.includes('/embed')) {
                    // If it's already an embed URL, ensure proper format
                    if (!cleanUrl.endsWith('/embed') && !cleanUrl.endsWith('/embed/')) {
                      cleanUrl = cleanUrl.endsWith('/') ? cleanUrl + 'embed' : cleanUrl + '/embed';
                    }
                    return cleanUrl;
                  }
                  
                  // Extract Reel ID from various Instagram URL formats
                  let reelId = null;
                  
                  // instagram.com/reel/REEL_ID or instagram.com/reel/REEL_ID/
                  // Match pattern: reel/ followed by alphanumeric and hyphens (like C4i-XcMtfDq)
                  const reelMatch = cleanUrl.match(/instagram\.com\/reel\/([A-Za-z0-9_-]+)/i);
                  if (reelMatch && reelMatch[1]) {
                    reelId = reelMatch[1];
                  }
                  
                  // instagram.com/p/REEL_ID (posts)
                  if (!reelId) {
                    const postMatch = cleanUrl.match(/instagram\.com\/p\/([A-Za-z0-9_-]+)/i);
                    if (postMatch && postMatch[1]) {
                      reelId = postMatch[1];
                    }
                  }
                  
                  // instagram.com/reels/REEL_ID (new format)
                  if (!reelId) {
                    const reelsMatch = cleanUrl.match(/instagram\.com\/reels\/([A-Za-z0-9_-]+)/i);
                    if (reelsMatch && reelsMatch[1]) {
                      reelId = reelsMatch[1];
                    }
                  }
                  
                  // Also check for data-instgrm-permalink attribute format
                  if (!reelId) {
                    const permalinkMatch = cleanUrl.match(/reel\/([A-Za-z0-9_-]+)/i);
                    if (permalinkMatch && permalinkMatch[1]) {
                      reelId = permalinkMatch[1];
                    }
                  }
                  
                  if (reelId) {
                    // Use Instagram's official embed URL format with parameters to prevent redirects
                    // Using minimal parameters to ensure compatibility
                    return `https://www.instagram.com/reel/${reelId}/embed/`;
                  }
                  
                  console.warn('Could not extract Reel ID from URL:', url);
                  return null;
                };

                const username = getInstagramUsername(reel.url);
                const embedUrl = getInstagramEmbedUrl(reel.url);
                
                // Debug: Log the URLs to verify correct extraction
                if (embedUrl && reel.url) {
                  console.log(`Reel ${index + 1}:`, {
                    originalUrl: reel.url,
                    extractedEmbedUrl: embedUrl,
                    reelId: embedUrl.match(/reel\/([^\/]+)/)?.[1]
                  });
                }
                
                return (
                  <div
                    key={index}
                    style={{
                      background: 'white',
                      borderRadius: '16px',
                      overflow: 'hidden',
                      border: '1px solid #e5e7eb',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.15)';
                      e.currentTarget.style.borderColor = '#3b82f6';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                      e.currentTarget.style.borderColor = '#e5e7eb';
                    }}
                  >
                    {/* Instagram Reel Embed */}
                    {embedUrl ? (
                      <div 
                        style={{
                          position: 'relative',
                          width: '100%',
                          paddingTop: '125%', // Taller aspect ratio for better video visibility (Instagram Reels are typically 9:16)
                          background: '#000',
                          overflow: 'hidden'
                        }}
                        data-reel-index={index}
                      >
                        <iframe
                          src={embedUrl + (embedUrl.includes('?') ? '&' : '?') + 'hidecaption=1'}
                          title={reel.title || `Instagram Reel ${index + 1}`}
                          key={`reel-${index}-${embedUrl}`}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            border: 'none',
                            borderRadius: '0',
                            pointerEvents: 'auto',
                            zIndex: 1
                          }}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                          scrolling="no"
                          frameBorder="0"
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                          data-reel-index={index}
                          data-reel-url={reel.url}
                          onError={(e) => {
                            console.error('Instagram embed error:', e);
                            // If embed fails, open original URL in new tab
                            if (reel.url) {
                              window.open(reel.url, '_blank', 'noopener,noreferrer');
                            }
                          }}
                        />
                      </div>
                    ) : (
                      <div style={{
                        position: 'relative',
                        width: '100%',
                        paddingTop: '56.25%', // 16:9 aspect ratio
                        background: 'linear-gradient(135deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {/* Instagram Icon with Play Button */}
                        <div style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '12px',
                          zIndex: 2
                        }}>
                          <div style={{
                            width: '60px',
                            height: '60px',
                            borderRadius: '16px',
                            background: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                          }}>
                            <img 
                              src="https://upload.wikimedia.org/wikipedia/commons/thumb/a/a5/Instagram_icon.png/1024px-Instagram_icon.png" 
                              alt="Instagram" 
                              style={{ width: '40px', height: '40px' }}
                            />
                          </div>
                          {/* Play Button */}
                          <div style={{
                            width: '50px',
                            height: '50px',
                            borderRadius: '50%',
                            background: 'rgba(255, 255, 255, 0.9)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                          }}>
                            <div style={{
                              width: 0,
                              height: 0,
                              borderLeft: '18px solid #262626',
                              borderTop: '11px solid transparent',
                              borderBottom: '11px solid transparent',
                              marginLeft: '5px'
                            }}></div>
                          </div>
                        </div>
                        {/* Reel Title Overlay */}
                        {reel.title && (
                          <div style={{
                            position: 'absolute',
                            bottom: '0',
                            left: '0',
                            width: '100%',
                            padding: '12px 16px',
                            background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)',
                            color: 'white',
                            fontSize: '0.9rem',
                            fontWeight: '600',
                            zIndex: 1
                          }}>
                            {reel.title}
                          </div>
                        )}
                      </div>
                    )}

                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Survey Section - Button Only */}
        {(() => {
          if (!profile.surveys || !Array.isArray(profile.surveys) || profile.surveys.length === 0) {
            return null;
          }
          
          // Filter out submitted surveys
          const availableSurveys = profile.surveys.filter((survey, surveyIndex) => {
            return !submittedSurveys[surveyIndex];
          });
          
          console.log('Survey Section Debug:');
          console.log('- Total surveys:', profile.surveys.length);
          console.log('- Submitted surveys state:', submittedSurveys);
          console.log('- Available surveys count:', availableSurveys.length);
          
          // Only show section if there are available surveys
          if (availableSurveys.length === 0) {
            console.log('- No available surveys, hiding section');
            return null;
          }
          
          console.log('- Showing survey section with', availableSurveys.length, 'surveys');
          
          return (
            <div style={{
              maxWidth: '800px',
              width: '100%',
              margin: '10px auto 24px',
              background: 'white',
              borderRadius: '20px',
              padding: '32px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
              position: 'relative',
              zIndex: 10,
              textAlign: 'center'
            }}>
              <h2 style={{
                fontSize: '1.3rem',
                color: '#1a1d29',
                marginBottom: '24px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px'
              }}>
                <span style={{ fontSize: '1.5rem' }}>📋</span>
                Survey (सर्वेक्षण)
              </h2>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center' }}>
                {profile.surveys.map((survey, surveyIndex) => {
                  const isSubmitted = submittedSurveys[surveyIndex];
                  
                  console.log(`Survey ${surveyIndex}: ${survey.title}, Submitted: ${isSubmitted}`);
                  
                  // Hide submitted surveys - don't render them at all
                  if (isSubmitted) {
                    return null;
                  }
                  
                  return (
                    <button
                      key={surveyIndex}
                      onClick={() => {
                        setSelectedSurvey({ survey, surveyIndex });
                        setShowSurveyModal(true);
                      }}
                      style={{
                        padding: '16px 32px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '12px',
                        fontSize: '1rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                        transition: 'all 0.3s',
                        minWidth: '250px',
                        position: 'relative'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
                      }}
                    >
                      📋 {survey.title}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Feedback Section - Button Only */}
        <div style={{
          maxWidth: '800px',
          width: '100%',
          margin: '10px auto 24px',
          background: 'white',
          borderRadius: '20px',
          padding: '32px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          position: 'relative',
          zIndex: 10,
          textAlign: 'center'
        }}>
          <h2 style={{
            fontSize: '1.3rem',
            color: '#1a1d29',
            marginBottom: '24px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px'
          }}>
            <span style={{ fontSize: '1.5rem' }}>💬</span>
            Share Your Feedback
          </h2>
          
          <button
            onClick={() => setShowFeedbackModal(true)}
            style={{
              padding: '16px 32px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
              transition: 'all 0.3s',
              minWidth: '250px'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
            }}
          >
            💬 Submit Feedback
          </button>
        </div>

        {/* Share Profile Link Section */}
        <div style={{
          maxWidth: '800px',
          width: '100%',
          margin: '10px auto 24px',
          background: 'white',
          borderRadius: '20px',
          padding: '32px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          position: 'relative',
          zIndex: 10,
          textAlign: 'center'
        }}>
          <h2 style={{
            fontSize: '1.3rem',
            color: '#1a1d29',
            marginBottom: '20px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px'
          }}>
            <span style={{ fontSize: '1.5rem' }}>📤</span>
            Share This Profile
          </h2>
          
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            alignItems: 'center'
          }}>
            {/* Profile Link Display */}
            <div style={{
              width: '100%',
              maxWidth: '600px',
              background: '#f8f9fa',
              border: '2px solid #e5e7eb',
              borderRadius: '12px',
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              wordBreak: 'break-all'
            }}>
              <span style={{ fontSize: '1.2rem' }}>🔗</span>
              <span style={{ 
                flex: 1, 
                fontSize: '0.9rem', 
                color: '#333',
                fontFamily: 'monospace'
              }}>
                {window.location.href}
              </span>
            </div>

            {/* WhatsApp Number Input */}
            <div style={{
              width: '100%',
              maxWidth: '600px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}>
              <label style={{
                fontSize: '0.9rem',
                fontWeight: '500',
                color: '#333',
                textAlign: 'left'
              }}>
                Enter WhatsApp Number (Optional):
              </label>
              <div style={{
                display: 'flex',
                gap: '8px',
                alignItems: 'stretch'
              }}>
                <input
                  type="tel"
                  placeholder="e.g., 919422076621 (with country code)"
                  value={whatsappNumber}
                  onChange={(e) => {
                    // Allow only numbers and + sign
                    const value = e.target.value.replace(/[^0-9+]/g, '');
                    setWhatsappNumber(value);
                  }}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '12px',
                    fontSize: '0.95rem',
                    outline: 'none',
                    transition: 'all 0.3s'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#667eea';
                    e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <button
                  onClick={() => setWhatsappNumber('')}
                  style={{
                    padding: '12px 16px',
                    background: '#f3f4f6',
                    border: '2px solid #e5e7eb',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontSize: '1.2rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#e5e7eb';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#f3f4f6';
                  }}
                  title="Clear"
                >
                  ✕
                </button>
              </div>
              {whatsappNumber && (
                <p style={{
                  fontSize: '0.85rem',
                  color: '#666',
                  margin: 0,
                  textAlign: 'left'
                }}>
                  {whatsappNumber.length < 10 ? '⚠️ Please enter a valid phone number' : '✓ Number entered'}
                </p>
              )}
            </div>

            {/* Share Buttons */}
            <div style={{
              display: 'flex',
              gap: '12px',
              flexWrap: 'wrap',
              justifyContent: 'center',
              width: '100%'
            }}>
              {/* Copy Link Button */}
              <button
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(window.location.href);
                    setShareMessage('Link copied to clipboard!');
                    setTimeout(() => setShareMessage(''), 3000);
                  } catch (err) {
                    // Fallback for older browsers
                    const textArea = document.createElement('textarea');
                    textArea.value = window.location.href;
                    textArea.style.position = 'fixed';
                    textArea.style.opacity = '0';
                    document.body.appendChild(textArea);
                    textArea.select();
                    try {
                      document.execCommand('copy');
                      setShareMessage('Link copied to clipboard!');
                      setTimeout(() => setShareMessage(''), 3000);
                    } catch (e) {
                      setShareMessage('Failed to copy. Please copy manually.');
                      setTimeout(() => setShareMessage(''), 3000);
                    }
                    document.body.removeChild(textArea);
                  }
                }}
                style={{
                  padding: '12px 24px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                  transition: 'all 0.3s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
                }}
              >
                📋 Copy Link
              </button>

              {/* WhatsApp Share Button */}
              <button
                onClick={() => {
                  // Format message with clickable link
                  const profileLink = window.location.href;
                  const message = `Check out ${profile.name}'s profile${profile.party_name ? ` - ${profile.party_name}` : ''}\n\n🔗 ${profileLink}`;
                  
                  // If phone number is entered, open direct chat with that number
                  if (whatsappNumber && whatsappNumber.trim()) {
                    // Clean the phone number (remove + and spaces, keep only digits)
                    const cleanNumber = whatsappNumber.replace(/[^0-9]/g, '');
                    
                    // Validate phone number (should be at least 10 digits)
                    if (cleanNumber.length >= 10) {
                      const whatsappUrl = `https://wa.me/${cleanNumber}?text=${encodeURIComponent(message)}`;
                      window.open(whatsappUrl, '_blank');
                    } else {
                      setShareMessage('Please enter a valid WhatsApp number (at least 10 digits)');
                      setTimeout(() => setShareMessage(''), 3000);
                    }
                  } else {
                    // If no number entered, open WhatsApp share dialog
                    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
                    window.open(whatsappUrl, '_blank');
                  }
                }}
                style={{
                  padding: '12px 24px',
                  background: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(37, 211, 102, 0.3)',
                  transition: 'all 0.3s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(37, 211, 102, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(37, 211, 102, 0.3)';
                }}
              >
                💬 WhatsApp
              </button>

              {/* Native Share Button (if available) */}
              {navigator.share && (
                <button
                  onClick={async () => {
                    try {
                      await navigator.share({
                        title: `${profile.name} - ${profile.party_name || 'Political Profile'}`,
                        text: `Check out ${profile.name}'s profile`,
                        url: window.location.href
                      });
                    } catch (err) {
                      if (err.name !== 'AbortError') {
                        console.error('Error sharing:', err);
                      }
                    }
                  }}
                  style={{
                    padding: '12px 24px',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                    transition: 'all 0.3s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(16, 185, 129, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                  }}
                >
                  📱 Share
                </button>
              )}
            </div>

            {/* Success Message */}
            {shareMessage && (
              <div style={{
                padding: '12px 20px',
                background: shareMessage.includes('Failed') ? '#fee2e2' : '#d1fae5',
                color: shareMessage.includes('Failed') ? '#991b1b' : '#065f46',
                borderRadius: '8px',
                fontSize: '0.9rem',
                fontWeight: '500',
                marginTop: '8px'
              }}>
                {shareMessage}
              </div>
            )}
          </div>
        </div>

        {/* Survey Modal/Popup */}
        {showSurveyModal && selectedSurvey && (() => {
          const { survey, surveyIndex } = selectedSurvey;
          const isSubmitted = submittedSurveys[surveyIndex];
          
          // Function to handle survey submission
          const handleSubmitSurvey = async () => {
            try {
              setSubmittingSurvey({ ...submittingSurvey, [surveyIndex]: true });
              setSurveyMessage({ ...surveyMessage, [surveyIndex]: null });

              // Collect all responses
              const responses = {};
              let allAnswered = true;

              survey.questions.forEach((question, qIndex) => {
                const questionName = `survey_modal_${surveyIndex}_q_${qIndex}`;
                
                if (question.type === 'text') {
                  const textarea = document.querySelector(`textarea[name="${questionName}"]`);
                  if (textarea && textarea.value.trim()) {
                    responses[qIndex] = {
                      question: question.question,
                      answer: textarea.value.trim()
                    };
                  } else {
                    allAnswered = false;
                  }
                } else if (question.type === 'yes_no' || question.type === 'multiple_choice') {
                  const radio = document.querySelector(`input[name="${questionName}"]:checked`);
                  if (radio) {
                    responses[qIndex] = {
                      question: question.question,
                      answer: radio.value
                    };
                  } else {
                    allAnswered = false;
                  }
                }
              });

              if (!allAnswered) {
                setSurveyMessage({ 
                  ...surveyMessage, 
                  [surveyIndex]: { type: 'error', text: 'Please answer all questions before submitting.' } 
                });
                setSubmittingSurvey({ ...submittingSurvey, [surveyIndex]: false });
                return;
              }

              // Submit to backend
              const response = await fetch(`${API_BASE_URL}/api/survey-responses/submit`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  profile_id: profile.id,
                  survey_index: surveyIndex,
                  survey_title: survey.title,
                  responses: responses,
                  respondent_identifier: respondentIdentifier
                })
              });

              const data = await response.json();

              if (response.ok && data.success) {
                // Mark survey as submitted in state (from database)
                setSubmittedSurveys({ ...submittedSurveys, [surveyIndex]: true });
                
                setSurveyMessage({ 
                  ...surveyMessage, 
                  [surveyIndex]: { type: 'success', text: 'Survey submitted successfully! Thank you for your feedback.' } 
                });
                
                // Close modal after 2 seconds
                setTimeout(() => {
                  setShowSurveyModal(false);
                  setSelectedSurvey(null);
                  setSurveyMessage({ ...surveyMessage, [surveyIndex]: null });
                }, 2000);
              } else {
                setSurveyMessage({ 
                  ...surveyMessage, 
                  [surveyIndex]: { type: 'error', text: data.message || 'Error submitting survey. Please try again.' } 
                });
              }
            } catch (error) {
              console.error('Error submitting survey:', error);
              setSurveyMessage({ 
                ...surveyMessage, 
                [surveyIndex]: { type: 'error', text: 'Error submitting survey. Please try again.' } 
              });
            } finally {
              setSubmittingSurvey({ ...submittingSurvey, [surveyIndex]: false });
            }
          };

          return (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999,
                padding: '20px'
              }}
              onClick={(e) => {
                if (e.target === e.currentTarget) {
                  setShowSurveyModal(false);
                  setSelectedSurvey(null);
                }
              }}
            >
              <div
                style={{
                  background: 'white',
                  borderRadius: '20px',
                  padding: '32px',
                  maxWidth: '600px',
                  width: '100%',
                  maxHeight: '90vh',
                  overflowY: 'auto',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                  position: 'relative'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Close Button */}
                <button
                  onClick={() => {
                    setShowSurveyModal(false);
                    setSelectedSurvey(null);
                  }}
                  style={{
                    position: 'absolute',
                    top: '16px',
                    right: '16px',
                    background: '#f3f4f6',
                    border: 'none',
                    borderRadius: '50%',
                    width: '36px',
                    height: '36px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.2rem',
                    color: '#64748b',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#e5e7eb';
                    e.target.style.color = '#1a1d29';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#f3f4f6';
                    e.target.style.color = '#64748b';
                  }}
                >
                  ×
                </button>

                {/* Survey Title */}
                <h2 style={{
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  color: '#1a1d29',
                  marginBottom: '12px',
                  paddingRight: '40px'
                }}>
                  {survey.title}
                </h2>
                
                {survey.description && (
                  <p style={{
                    color: '#64748b',
                    fontSize: '1rem',
                    marginBottom: '24px',
                    lineHeight: '1.6'
                  }}>
                    {survey.description}
                  </p>
                )}

                {isSubmitted ? (
                  <div style={{
                    padding: '24px',
                    background: '#d1fae5',
                    borderRadius: '12px',
                    textAlign: 'center',
                    border: '2px solid #10b981'
                  }}>
                    <div style={{ fontSize: '3rem', marginBottom: '12px' }}>✅</div>
                    <p style={{ fontSize: '1.1rem', color: '#065f46', fontWeight: '600', margin: 0 }}>
                      You have already submitted this survey. Thank you!
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Questions */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginBottom: '24px' }}>
                      {survey.questions && survey.questions.map((question, qIndex) => (
                        <div
                          key={qIndex}
                          style={{
                            background: '#f8f9fa',
                            borderRadius: '12px',
                            padding: '20px',
                            border: '1px solid #e5e7eb'
                          }}
                        >
                          <label style={{
                            display: 'block',
                            color: '#1a1d29',
                            fontWeight: '600',
                            marginBottom: '12px',
                            fontSize: '1rem'
                          }}>
                            {qIndex + 1}. {question.question}
                          </label>
                          
                          {question.type === 'text' && (
                            <textarea
                              name={`survey_modal_${surveyIndex}_q_${qIndex}`}
                              placeholder="Your answer..."
                              rows="3"
                              style={{
                                width: '100%',
                                padding: '12px',
                                border: '1px solid #d1d5db',
                                borderRadius: '8px',
                                fontSize: '0.95rem',
                                fontFamily: 'inherit',
                                resize: 'vertical'
                              }}
                            />
                          )}
                          
                          {question.type === 'yes_no' && (
                            <div style={{ display: 'flex', gap: '12px' }}>
                              <label style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                cursor: 'pointer',
                                padding: '10px 20px',
                                background: '#f3f4f6',
                                borderRadius: '8px',
                                transition: 'all 0.2s'
                              }}>
                                <input type="radio" name={`survey_modal_${surveyIndex}_q_${qIndex}`} value="yes" />
                                <span>Yes</span>
                              </label>
                              <label style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                cursor: 'pointer',
                                padding: '10px 20px',
                                background: '#f3f4f6',
                                borderRadius: '8px',
                                transition: 'all 0.2s'
                              }}>
                                <input type="radio" name={`survey_modal_${surveyIndex}_q_${qIndex}`} value="no" />
                                <span>No</span>
                              </label>
                            </div>
                          )}
                          
                          {question.type === 'multiple_choice' && question.options && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                              {question.options.map((option, optIndex) => (
                                <label
                                  key={optIndex}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    cursor: 'pointer',
                                    padding: '12px 16px',
                                    background: '#fff',
                                    borderRadius: '8px',
                                    border: '1px solid #e5e7eb',
                                    transition: 'all 0.2s'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = '#f3f4f6';
                                    e.currentTarget.style.borderColor = '#3b82f6';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = '#fff';
                                    e.currentTarget.style.borderColor = '#e5e7eb';
                                  }}
                                >
                                  <input
                                    type="radio"
                                    name={`survey_modal_${surveyIndex}_q_${qIndex}`}
                                    value={option}
                                  />
                                  <span style={{ color: '#1a1d29', fontSize: '0.95rem' }}>{option}</span>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Submit Button */}
                    <button
                      style={{
                        width: '100%',
                        padding: '14px 24px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '12px',
                        fontSize: '1rem',
                        fontWeight: '600',
                        cursor: submittingSurvey[surveyIndex] ? 'not-allowed' : 'pointer',
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                        transition: 'all 0.3s',
                        opacity: submittingSurvey[surveyIndex] ? 0.7 : 1
                      }}
                      onClick={handleSubmitSurvey}
                      disabled={submittingSurvey[surveyIndex]}
                    >
                      {submittingSurvey[surveyIndex] ? 'Submitting...' : 'Submit Survey'}
                    </button>

                    {/* Success/Error Message */}
                    {surveyMessage[surveyIndex] && (
                      <div style={{
                        marginTop: '16px',
                        padding: '12px 16px',
                        background: surveyMessage[surveyIndex].type === 'success' ? '#d1fae5' : '#fee2e2',
                        color: surveyMessage[surveyIndex].type === 'success' ? '#065f46' : '#991b1b',
                        borderRadius: '8px',
                        fontSize: '0.9rem',
                        border: `1px solid ${surveyMessage[surveyIndex].type === 'success' ? '#10b981' : '#ef4444'}`
                      }}>
                        {surveyMessage[surveyIndex].text}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })()}

        {/* Feedback Modal/Popup */}
        {showFeedbackModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000,
              padding: '20px'
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                // Don't reset form when closing via background click - let user keep their data
                setShowFeedbackModal(false);
              }
            }}
          >
            <div
              style={{
                background: 'white',
                borderRadius: '20px',
                padding: '32px',
                maxWidth: '600px',
                width: '100%',
                maxHeight: '90vh',
                overflowY: 'auto',
                position: 'relative',
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={() => {
                  setShowFeedbackModal(false);
                  setFeedbackForm({ name: '', mobile_number: '', address: '', description: '' });
                  setFeedbackImageUrls([]); // Reset image URLs state
                  feedbackImageUrlsRef.current = []; // Reset image URLs ref
                  setFeedbackImageFiles([]);
                  setFeedbackImagePreviews([]);
                  setFeedbackMessage(null);
                  // Reset file input
                  if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }
                }}
                style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  border: 'none',
                  background: '#f3f4f6',
                  color: '#64748b',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s',
                  lineHeight: '1'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#e5e7eb';
                  e.target.style.color = '#1a1d29';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#f3f4f6';
                  e.target.style.color = '#64748b';
                }}
              >
                ×
              </button>

              {/* Modal Title */}
              <h2 style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: '#1a1d29',
                marginBottom: '24px',
                paddingRight: '40px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <span style={{ fontSize: '1.8rem' }}>💬</span>
                Share Your Feedback
              </h2>

              {/* Feedback Form */}
              <form onSubmit={handleSubmitFeedback}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', color: '#1a1d29', fontWeight: '500', fontSize: '0.9rem' }}>
                        Name *
                      </label>
                      <input
                        type="text"
                        value={feedbackForm.name}
                        onChange={(e) => setFeedbackForm({ ...feedbackForm, name: e.target.value })}
                        placeholder="Enter your name"
                        required
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          background: '#f8f9fa',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          color: '#1a1d29',
                          fontSize: '0.95rem'
                        }}
                      />
                    </div>
                    
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', color: '#1a1d29', fontWeight: '500', fontSize: '0.9rem' }}>
                        Mobile Number *
                      </label>
                      <input
                        type="tel"
                        value={feedbackForm.mobile_number}
                        onChange={(e) => setFeedbackForm({ ...feedbackForm, mobile_number: e.target.value })}
                        placeholder="Enter mobile number"
                        required
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          background: '#f8f9fa',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          color: '#1a1d29',
                          fontSize: '0.95rem'
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', color: '#1a1d29', fontWeight: '500', fontSize: '0.9rem' }}>
                      Address *
                    </label>
                    <input
                      type="text"
                      value={feedbackForm.address}
                      onChange={(e) => setFeedbackForm({ ...feedbackForm, address: e.target.value })}
                      placeholder="Enter your address"
                      required
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        background: '#f8f9fa',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        color: '#1a1d29',
                        fontSize: '0.95rem'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', color: '#1a1d29', fontWeight: '500', fontSize: '0.9rem' }}>
                      Upload Images (Optional, Max 5MB each)
                      {uploadingFeedbackImage && (
                        <span style={{ marginLeft: '8px', color: '#667eea', fontSize: '0.85rem' }}>
                          (Uploading...)
                        </span>
                      )}
                    </label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleFeedbackImageChange}
                      disabled={uploadingFeedbackImage}
                      style={{
                        width: '100%',
                        padding: '10px',
                        background: uploadingFeedbackImage ? '#f3f4f6' : '#f8f9fa',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        color: '#1a1d29',
                        fontSize: '0.9rem',
                        cursor: uploadingFeedbackImage ? 'not-allowed' : 'pointer'
                      }}
                    />
                    {feedbackImagePreviews.length > 0 && (
                      <div style={{ marginTop: '12px', display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                        {feedbackImagePreviews.map((preview, index) => (
                          <div key={index} style={{ position: 'relative', display: 'inline-block' }}>
                            <img
                              src={preview}
                              alt={`Preview ${index + 1}`}
                              style={{
                                width: '120px',
                                height: '120px',
                                objectFit: 'cover',
                                borderRadius: '8px',
                                border: '1px solid #e5e7eb'
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveFeedbackImage(index)}
                              style={{
                                position: 'absolute',
                                top: '-8px',
                                right: '-8px',
                                width: '24px',
                                height: '24px',
                                borderRadius: '50%',
                                background: '#ef4444',
                                color: 'white',
                                border: '2px solid white',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {feedbackImageUrls.length > 0 && (
                      <div style={{ marginTop: '8px', padding: '8px', background: '#d1fae5', borderRadius: '6px', border: '1px solid #10b981' }}>
                        <p style={{ color: '#065f46', fontSize: '0.85rem', margin: 0 }}>
                          ✓ {feedbackImageUrls.length} image(s) uploaded successfully
                        </p>
                      </div>
                    )}
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', color: '#1a1d29', fontWeight: '500', fontSize: '0.9rem' }}>
                      Description *
                    </label>
                    <textarea
                      value={feedbackForm.description}
                      onChange={(e) => setFeedbackForm({ ...feedbackForm, description: e.target.value })}
                      placeholder="Enter your feedback"
                      rows="4"
                      required
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        background: '#f8f9fa',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        color: '#1a1d29',
                        fontSize: '0.95rem',
                        resize: 'vertical',
                        fontFamily: 'inherit'
                      }}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submittingFeedback}
                    style={{
                      width: '100%',
                      padding: '14px 24px',
                      background: submittingFeedback ? '#cbd5e1' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '12px',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: submittingFeedback ? 'not-allowed' : 'pointer',
                      boxShadow: submittingFeedback ? 'none' : '0 4px 12px rgba(102, 126, 234, 0.3)',
                      transition: 'all 0.3s',
                      opacity: submittingFeedback ? 0.7 : 1,
                      marginTop: '8px'
                    }}
                  >
                    {submittingFeedback ? 'Submitting...' : 'Submit Feedback'}
                  </button>

                  {feedbackMessage && (
                    <div style={{
                      padding: '12px 16px',
                      background: feedbackMessage.type === 'success' ? '#d1fae5' : '#fee2e2',
                      color: feedbackMessage.type === 'success' ? '#065f46' : '#991b1b',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      border: `1px solid ${feedbackMessage.type === 'success' ? '#10b981' : '#ef4444'}`
                    }}>
                      {feedbackMessage.text}
                    </div>
                  )}
                </div>
              </form>
            </div>
          </div>
        )}

      </div>

      {/* Footer */}
      <footer style={{
        background: 'white',
        color: '#1a1d29',
        padding: '12px 20px 10px 20px',
        marginTop: '40px',
        borderTop: '2px solid #e5e7eb',
        position: 'sticky',
        bottom: 0,
        zIndex: 1000,
        boxShadow: '0 -4px 12px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '6px',
          flexWrap: 'wrap'
        }}>
          {/* Home Button */}
          <button
            onClick={() => {
              const homeSection = document.getElementById('home');
              if (homeSection) {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              padding: '8px 16px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              borderRadius: '8px',
              transition: 'all 0.3s',
              color: '#3b82f6'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#f0f4ff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ color: '#3b82f6' }}>
              <path d="M3 12L5 10M5 10L12 3L19 10M5 10V20C5 20.5304 5.21071 21.0391 5.58579 21.4142C5.96086 21.7893 6.46957 22 7 22H17C17.5304 22 18.0391 21.7893 18.4142 21.4142C18.7893 21.0391 19 20.5304 19 20V10M19 10L21 12M9 21V16C9 15.4696 9.21071 14.9609 9.58579 14.5858C9.96086 14.2107 10.4696 14 11 14H13C13.5304 14 14.0391 14.2107 14.4142 14.5858C14.7893 14.9609 15 15.4696 15 16V21" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span style={{ fontSize: '0.75rem', fontWeight: '500', color: '#3b82f6' }}>Home</span>
          </button>

          {/* About Button */}
          <button
            onClick={() => {
              const aboutSection = document.getElementById('about');
              if (aboutSection) {
                aboutSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              padding: '8px 16px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              borderRadius: '8px',
              transition: 'all 0.3s',
              color: '#3b82f6'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#f0f4ff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span style={{ fontSize: '0.75rem', fontWeight: '500', color: '#3b82f6' }}>About</span>
          </button>

          {/* Photos Button */}
          <button
            onClick={() => {
              const photosSection = document.getElementById('photos');
              if (photosSection) {
                photosSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              padding: '8px 16px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              borderRadius: '8px',
              transition: 'all 0.3s',
              color: '#3b82f6'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#f0f4ff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 16L8.586 11.414C9.367 10.633 10.633 10.633 11.414 11.414L16 16M14 14L15.586 12.414C16.367 11.633 17.633 11.633 18.414 12.414L22 16M18 8H18.01M6 20H18C19.1046 20 20 19.1046 20 18V6C20 4.89543 19.1046 4 18 4H6C4.89543 4 4 4.89543 4 6V18C4 19.1046 4.89543 20 6 20Z" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span style={{ fontSize: '0.75rem', fontWeight: '500', color: '#3b82f6' }}>Photos</span>
          </button>

          {/* Video Gallery Button */}
          {profile.video_gallery && profile.video_gallery.length > 0 && (
            <button
              onClick={() => {
                const videoGallerySection = document.getElementById('video-gallery');
                if (videoGallerySection) {
                  videoGallerySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                padding: '8px 16px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                borderRadius: '8px',
                transition: 'all 0.3s',
                color: '#3b82f6'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f0f4ff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 10L9 6V18L15 14V10Z" fill="#3b82f6"/>
                <path d="M21 3H3C1.89543 3 1 3.89543 1 5V19C1 20.1046 1.89543 21 3 21H21C22.1046 21 23 20.1046 23 19V5C23 3.89543 22.1046 3 21 3Z" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span style={{ fontSize: '0.75rem', fontWeight: '500', color: '#3b82f6' }}>Video Gallery</span>
            </button>
          )}

          {/* News Button */}
          <button
            onClick={() => {
              const newsSection = document.getElementById('news');
              if (newsSection) {
                newsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              padding: '8px 16px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              borderRadius: '8px',
              transition: 'all 0.3s',
              color: '#3b82f6'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#f0f4ff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 20H5C4.44772 20 4 19.5523 4 19V7C4 6.44772 4.44772 6 5 6H19C19.5523 6 20 6.44772 20 7V19C20 19.5523 19.5523 20 19 20Z" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M4 10H20M9 6V4C9 3.44772 9.44772 3 10 3H14C14.5523 3 15 3.44772 15 4V6" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M8 14H16M8 17H12" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span style={{ fontSize: '0.75rem', fontWeight: '500', color: '#3b82f6' }}>News</span>
          </button>

          {/* YouTube Button */}
          {profile.social_links?.youtube && profile.social_links.youtube.trim() !== '' ? (
            <button
              onClick={() => {
                if (profile.social_links.youtube && profile.social_links.youtube.trim() !== '') {
                  window.open(profile.social_links.youtube, '_blank', 'noopener,noreferrer');
                }
              }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                padding: '8px 16px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                borderRadius: '8px',
                transition: 'all 0.3s',
                color: '#3b82f6'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f0f4ff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.54 6.42C22.4212 5.94541 22.1793 5.51057 21.8387 5.15941C21.4981 4.80824 21.0697 4.55318 20.6 4.42C18.88 4 12 4 12 4C12 4 5.12 4 3.4 4.42C2.93025 4.55318 2.50186 4.80824 2.16126 5.15941C1.82066 5.51057 1.57879 5.94541 1.46 6.42C1.14521 8.186 0.991203 9.97647 1 11.77C0.991203 13.5635 1.14521 15.354 1.46 17.12C1.57879 17.5946 1.82066 18.0294 2.16126 18.3806C2.50186 18.7318 2.93025 18.9868 3.4 19.12C5.12 19.54 12 19.54 12 19.54C12 19.54 18.88 19.54 20.6 19.12C21.0697 18.9868 21.4981 18.7318 21.8387 18.3806C22.1793 18.0294 22.4212 17.5946 22.54 17.12C22.8548 15.354 23.0088 13.5635 23 11.77C23.0088 9.97647 22.8548 8.186 22.54 6.42Z" fill="#3b82f6"/>
                <path d="M9.75 15.02L15.5 11.77L9.75 8.52V15.02Z" fill="white"/>
              </svg>
              <span style={{ fontSize: '0.75rem', fontWeight: '500', color: '#3b82f6' }}>YouTube</span>
            </button>
          ) : null}

          {/* Instagram Button */}
          {profile.social_links?.instagram && profile.social_links.instagram.trim() !== '' ? (
            <button
              onClick={() => {
                if (profile.social_links.instagram && profile.social_links.instagram.trim() !== '') {
                  window.open(profile.social_links.instagram, '_blank', 'noopener,noreferrer');
                }
              }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                padding: '8px 16px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                borderRadius: '8px',
                transition: 'all 0.3s',
                color: '#3b82f6'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f0f4ff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4 2.209 0 4 1.791 4 4 0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" fill="#3b82f6"/>
              </svg>
              <span style={{ fontSize: '0.75rem', fontWeight: '500', color: '#3b82f6' }}>Instagram</span>
            </button>
          ) : null}
        </div>
        
      </footer>

    </div>
  );
};

export default ProfileView;
